import React, { memo } from "react";
import { Box, Flex, Text, Image as ChakraImage, Tooltip } from "@chakra-ui/react";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { InlineMath } from "react-katex";
import "katex/dist/katex.min.css";
import { a } from "../appColors";

/**
 * The answer text with its formulas drawn as maths.
 *
 * Formulas travel inside the text as `$$latex$$`; shown raw, a student who
 * wrote a fraction got their own LaTeX back. Splitting on a capture group
 * keeps the formulas at the odd indices.
 */
const AnswerText = ({ text }) =>
  text.split(/\$\$(.+?)\$\$/gs).map((part, i) =>
    i % 2 === 1 ? (
      <InlineMath key={i} math={part} renderError={() => <span>{`$$${part}$$`}</span>} />
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );

/**
 * While the marker is reading.
 *
 * Submitting used to leave the card looking exactly as it did before, so
 * nothing said the answer had gone anywhere. The words move rather than a
 * spinner: it says what is happening, not merely that something is.
 */
const MarkingNotice = () => (
  <Flex align="center" gap="9px" px="19px" py="18px">
    <Box
      boxSize="7px"
      borderRadius="full"
      bg={a.accent}
      sx={{ animation: "standePulse 1.1s ease-in-out infinite" }}
    />
    <Text
      fontSize="12px"
      color={a.textSub}
      sx={{ animation: "standeBreathe 1.8s ease-in-out infinite" }}
    >
      Marking your answer…
    </Text>
  </Flex>
);

/**
 * An answer that has been handed in (Figma 227:119).
 *
 * The same card the student typed into, showing what they said rather than
 * an empty field. Coming back to a paper should look like coming back to
 * your own work, not like starting again.
 *
 * Editing is offered, not assumed: a student may resubmit, a teacher reading
 * a marked script may not, and review mode passes no handler at all.
 */
const SubmittedAnswer = ({ text, attachments = [], onEdit, isMarking = false, children }) => (
  <Box
    bg={a.greySurface}
    border="1px solid"
    borderColor={a.edge}
    borderRadius="14px"
    overflow="hidden"
    width="100%"
  >
    <Box px="19px" pt="16px" pb={children || isMarking ? "12px" : "16px"}>
      <Flex align="center" justify="space-between" gap="10px" mb="8px">
        <Text fontSize="12px" fontWeight="600" color={a.text}>
          My Answer :
        </Text>

        {onEdit && !isMarking && (
          <Tooltip label="Edit and submit again" placement="top" hasArrow fontSize="11px">
            <Flex
              as="button"
              type="button"
              aria-label="Edit your answer"
              align="center"
              gap="5px"
              px="10px"
              h="24px"
              borderRadius="200px"
              border="1px solid"
              borderColor={a.edge}
              color={a.text}
              fontSize="11px"
              flexShrink={0}
              cursor="pointer"
              transition="background 0.12s"
              _hover={{ bg: a.hover, borderColor: a.text }}
              _focusVisible={{ outline: "2px solid", outlineColor: a.text, outlineOffset: "2px" }}
              onClick={onEdit}
            >
              <PencilSquareIcon style={{ width: 13, height: 13 }} />
              <Text as="span">Edit</Text>
            </Flex>
          </Tooltip>
        )}
      </Flex>

      {text?.trim() ? (
        <Text fontSize="12px" lineHeight="19px" color={a.text2} whiteSpace="pre-wrap">
          <AnswerText text={text} />
        </Text>
      ) : (
        <Text fontSize="12px" color={a.textMuted} fontStyle="italic">
          No written answer
        </Text>
      )}

      {attachments.length > 0 && (
        <Flex gap="12px" mt="14px" flexWrap="wrap">
          {attachments.map((att, i) => (
            <Box
              key={att.id || att.url || i}
              maxW="min(320px, 100%)"
              borderRadius="10px"
              overflow="hidden"
              border="1px solid"
              borderColor={a.edge}
              bg={a.surface}
              flexShrink={0}
            >
              {/* Big enough to read, and whole: a drawing is the answer
                  here, so cropping it to a thumbnail hid the working. */}
              <ChakraImage
                src={att.url || att.image?.src}
                alt={att.name || "Attached image"}
                display="block"
                maxW="100%"
                maxH="240px"
                objectFit="contain"
              />
            </Box>
          ))}
        </Flex>
      )}
    </Box>

    {/* Where the marker's verdict goes, when there is one. Passed in rather
        than built here, so the paper decides what marking looks like.
        Inside the same inset as the answer: it was rendering flush to the
        card's edge while the words above it were indented. */}
    {isMarking ? (
      <MarkingNotice />
    ) : (
      children && (
        <Box px="19px" pb="16px">
          {children}
        </Box>
      )
    )}
  </Box>
);

export default memo(SubmittedAnswer);
